/**
 * ReferenceImageUpload Component
 *
 * Handles uploading and managing reference images for a person.
 * Supports drag-and-drop, file validation, and image removal.
 */

import { useState, useCallback, useRef } from 'react';
import { X, Loader2, ImagePlus, AlertCircle } from 'lucide-react';
import type { IReferenceImageUploadProps } from '@/@types/referencePersons';
import {
  REFERENCE_PERSON_CONSTANTS,
  validateImageFile,
  formatImageCount,
} from '@/@types/referencePersons';
import type { Id } from '../../../convex/_generated/dataModel';
import { useReferencePersons } from '@/hooks/useReferencePersons';

function ReferenceImageUpload({
  personId,
  currentImages,
  maxImages,
  onImageAdded,
  onImageRemoved,
  disabled = false,
}: IReferenceImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { addImage, removeImage } = useReferencePersons();

  const canUpload = currentImages.length < maxImages && !disabled;
  const remainingSlots = maxImages - currentImages.length;

  const handleFileUpload = useCallback(
    async (files: FileList | File[]) => {
      setError(null);

      const fileArray = Array.from(files);
      const validFiles: File[] = [];

      // Validate each file
      for (const file of fileArray) {
        const validation = validateImageFile(file);
        if (!validation.valid) {
          setError(validation.error || 'Invalid file');
          continue;
        }
        validFiles.push(file);
      }

      // Check if we'll exceed the limit
      if (currentImages.length + validFiles.length > maxImages) {
        const canAccept = maxImages - currentImages.length;
        setError(`Can only add ${canAccept} more image${canAccept !== 1 ? 's' : ''}`);
        validFiles.splice(canAccept);
      }

      if (validFiles.length === 0) {
        return;
      }

      // Upload each valid file
      for (const file of validFiles) {
        const fileKey = `${file.name}-${file.size}`;
        setUploadingFiles(prev => new Set(prev).add(fileKey));

        try {
          const result = await addImage(personId, file);
          if (result.success && result.storageId) {
            onImageAdded(result.storageId);
          } else if (result.error) {
            setError(result.error);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to upload image');
        } finally {
          setUploadingFiles(prev => {
            const next = new Set(prev);
            next.delete(fileKey);
            return next;
          });
        }
      }
    },
    [personId, currentImages.length, maxImages, addImage, onImageAdded]
  );

  const handleRemoveImage = useCallback(
    async (storageId: Id<'_storage'>) => {
      setError(null);
      setRemovingIds(prev => new Set(prev).add(storageId));

      try {
        const result = await removeImage(personId, storageId);
        if (result.success) {
          onImageRemoved(storageId);
        } else if (result.error) {
          setError(result.error);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove image');
      } finally {
        setRemovingIds(prev => {
          const next = new Set(prev);
          next.delete(storageId);
          return next;
        });
      }
    },
    [personId, removeImage, onImageRemoved]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (canUpload) {
        setIsDragging(true);
      }
    },
    [canUpload]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (!canUpload) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileUpload(files);
      }
    },
    [canUpload, handleFileUpload]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileUpload(files);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFileUpload]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const isUploading = uploadingFiles.size > 0;

  return (
    <div className="space-y-4">
      {/* Current Images Grid */}
      {currentImages.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {currentImages.map(image => {
            const isRemoving = removingIds.has(image.id);
            return (
              <div key={image.id} className="relative group aspect-square">
                <img
                  src={image.url}
                  alt="Reference"
                  className={`w-full h-full object-cover rounded-lg border border-gray-200 dark:border-gray-700 ${
                    isRemoving ? 'opacity-50' : ''
                  }`}
                />
                {!disabled && !isRemoving && (
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(image.id)}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                    aria-label="Remove image"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                {isRemoving && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Area */}
      {canUpload && (
        <div
          role="button"
          tabIndex={!isUploading && !disabled ? 0 : -1}
          aria-disabled={disabled || isUploading}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
              : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
          } ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={!isUploading ? handleUploadClick : undefined}
          onKeyDown={
            !isUploading && !disabled
              ? e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleUploadClick();
                  }
                }
              : undefined
          }
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={REFERENCE_PERSON_CONSTANTS.ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled || isUploading}
          />

          {isUploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Uploading {uploadingFiles.size} image{uploadingFiles.size !== 1 ? 's' : ''}...
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full mb-3">
                <ImagePlus className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Drop images here or click to upload
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                JPEG or PNG, max {REFERENCE_PERSON_CONSTANTS.MAX_IMAGE_SIZE_MB}MB each
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining
              </p>
            </div>
          )}
        </div>
      )}

      {/* Image Count */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500 dark:text-gray-400">
          {formatImageCount(currentImages.length, maxImages)}
        </span>
        {currentImages.length >= maxImages && (
          <span className="text-amber-600 dark:text-amber-400">Maximum images reached</span>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400 rounded-md">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Tips */}
      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p>
          <strong>Tips for best results:</strong>
        </p>
        <ul className="list-disc list-inside space-y-0.5 pl-2">
          <li>Use 3-5 photos with different angles (front, side, 3/4 view)</li>
          <li>Ensure the face is clearly visible and well-lit</li>
          <li>Use consistent lighting across photos</li>
          <li>Higher resolution photos produce better results</li>
        </ul>
      </div>
    </div>
  );
}

export { ReferenceImageUpload };
